import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';

function Appointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [patients, setPatients] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [formData, setFormData] = useState({
    patientId: '',
    practitionerId: '',
    appointmentDate: format(new Date(), 'yyyy-MM-dd'),
    startTime: '09:00',
    endTime: '09:30',
    type: 'follow_up',
    appointmentNotes: ''
  });

  useEffect(() => {
    fetchAppointments();
    fetchPatientsAndPractitioners();
  }, []);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const res = await api.get('/appointments');
      setAppointments(res.appointments || []);
    } catch (error) {
      toast.error('Failed to load appointments');
    }
    setLoading(false);
  };

  const fetchPatientsAndPractitioners = async () => {
    try {
      const [patientsRes, usersRes] = await Promise.all([
        api.get('/patients'),
        api.get('/auth/me')
      ]);
      setPatients(patientsRes.patients || []);
      const allPractitioners = [];
      if (usersRes.user) allPractitioners.push(usersRes.user);
      if (usersRes.user?.clinic?.users) {
        usersRes.user.clinic.users.forEach(u => {
          if (!allPractitioners.find(p => p.id === u.id)) allPractitioners.push(u);
        });
      }
      setPractitioners(allPractitioners);
    } catch (error) {
      console.error('Failed to load patients/practitioners');
    }
  };

  const handleOpenNewAppointment = () => {
    setFormData({
      patientId: '',
      practitionerId: practitioners[0]?.id || '',
      appointmentDate: format(new Date(), 'yyyy-MM-dd'),
      startTime: '09:00',
      endTime: '09:30',
      type: 'follow_up',
      appointmentNotes: ''
    });
    setShowModal(true);
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    if (!formData.patientId || !formData.practitionerId) {
      toast.error('Please select a patient and practitioner');
      return;
    }
    try {
      await api.post('/appointments', formData);
      toast.success('Appointment created');
      setShowModal(false);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create appointment');
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await api.put(`/appointments/${id}/status`, { status });
      toast.success('Status updated');
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleCancel = async (id) => {
    if (window.confirm('Are you sure you want to cancel this appointment?')) {
      try {
        await api.put(`/appointments/${id}/cancel`, { reason: 'Cancelled by staff' });
        toast.success('Appointment cancelled');
        fetchAppointments();
      } catch (error) {
        toast.error('Failed to cancel appointment');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: '#3498db',
      confirmed: '#2ecc71',
      checked_in: '#f39c12',
      in_progress: '#9b59b6',
      completed: '#27ae60',
      cancelled: '#e74c3c',
      no_show: '#95a5a6'
    };
    return colors[status] || '#3498db';
  };

  const getTypeLabel = (type) => {
    const labels = {
      initial_assessment: 'Initial Assessment',
      follow_up: 'Follow Up',
      treatment: 'Treatment',
      review: 'Review',
      discharge: 'Discharge'
    };
    return labels[type] || type;
  };

  const filteredAppointments = appointments.filter(apt => {
    if (filter !== 'all' && apt.status !== filter) return false;
    if (dateFilter && apt.appointmentDate !== dateFilter) return false;
    return true;
  }).sort((a, b) => {
    const dateCompare = b.appointmentDate.localeCompare(a.appointmentDate);
    if (dateCompare !== 0) return dateCompare;
    return a.startTime.localeCompare(b.startTime);
  });

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading appointments...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0 }}>Appointments</h1>
        <button onClick={handleOpenNewAppointment} className="btn btn-primary">
          + New Appointment
        </button>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <label className="form-label">Status</label>
            <select 
              className="form-input" 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              style={{ minWidth: '150px' }}
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="confirmed">Confirmed</option>
              <option value="checked_in">Checked In</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no_show">No Show</option>
            </select>
          </div>
          <div>
            <label className="form-label">Date</label>
            <input 
              type="date" 
              className="form-input"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
          </div>
          {(filter !== 'all' || dateFilter) && (
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button 
                className="btn btn-secondary"
                onClick={() => { setFilter('all'); setDateFilter(''); }}
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {filteredAppointments.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-light)' }}>
            <p>No appointments found</p>
            <Link to="/calendar" style={{ color: 'var(--primary)' }}>
              Create your first appointment
            </Link>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Date & Time</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Patient</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Type</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAppointments.map(apt => (
                <tr key={apt.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontWeight: 500 }}>
                      {format(parseISO(apt.appointmentDate), 'EEE, MMM d, yyyy')}
                    </div>
                    <div style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
                      {apt.startTime} - {apt.endTime}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {apt.patient ? (
                      <Link 
                        to={`/patients/${apt.patient.id}`}
                        style={{ color: 'var(--primary)', textDecoration: 'none' }}
                      >
                        {apt.patient.firstName} {apt.patient.lastName}
                      </Link>
                    ) : (
                      <span style={{ color: 'var(--text-light)' }}>Unknown</span>
                    )}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {getTypeLabel(apt.type)}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '999px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      background: getStatusColor(apt.status),
                      color: 'white',
                      textTransform: 'capitalize'
                    }}>
                      {apt.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {apt.status !== 'cancelled' && apt.status !== 'completed' && (
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {apt.status === 'scheduled' && (
                          <button 
                            className="btn btn-secondary" 
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => handleStatusUpdate(apt.id, 'confirmed')}
                          >
                            Confirm
                          </button>
                        )}
                        {apt.status === 'confirmed' && (
                          <button 
                            className="btn btn-secondary" 
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => handleStatusUpdate(apt.id, 'checked_in')}
                          >
                            Check In
                          </button>
                        )}
                        {apt.status === 'checked_in' && (
                          <button 
                            className="btn btn-secondary" 
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => handleStatusUpdate(apt.id, 'in_progress')}
                          >
                            Start
                          </button>
                        )}
                        {apt.status === 'in_progress' && (
                          <button 
                            className="btn btn-primary" 
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => handleStatusUpdate(apt.id, 'completed')}
                          >
                            Complete
                          </button>
                        )}
                        <button 
                          className="btn btn-secondary" 
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', color: '#e74c3c' }}
                          onClick={() => handleCancel(apt.id)}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: '1rem', color: 'var(--text-light)', fontSize: '0.875rem' }}>
        Showing {filteredAppointments.length} of {appointments.length} appointments
      </div>

      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }} onClick={() => setShowModal(false)}>
          <div className="card" style={{ width: '100%', maxWidth: '500px', margin: '1rem' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>New Appointment</h2>
            <form onSubmit={handleCreateAppointment}>
              <div className="form-group">
                <label className="form-label">Patient *</label>
                <select
                  className="form-input"
                  value={formData.patientId}
                  onChange={(e) => setFormData({ ...formData, patientId: e.target.value })}
                  required
                >
                  <option value="">Select a patient</option>
                  {patients.map(p => (
                    <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Practitioner *</label>
                <select
                  className="form-input"
                  value={formData.practitionerId}
                  onChange={(e) => setFormData({ ...formData, practitionerId: e.target.value })}
                  required
                >
                  <option value="">Select a practitioner</option>
                  {practitioners.map(p => (
                    <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.appointmentDate}
                  onChange={(e) => setFormData({ ...formData, appointmentDate: e.target.value })}
                  required
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Start Time *</label>
                  <input
                    type="time"
                    className="form-input"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">End Time *</label>
                  <input
                    type="time"
                    className="form-input"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select
                  className="form-input"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="initial_assessment">Initial Assessment</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="home_visit">Home Visit</option>
                  <option value="video_consultation">Video Consultation</option>
                  <option value="group_session">Group Session</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-input"
                  rows={2}
                  value={formData.appointmentNotes}
                  onChange={(e) => setFormData({ ...formData, appointmentNotes: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" className="btn btn-primary">Create Appointment</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Appointments;
